/**
 * ContainerChildren — Renders a container's child sections with a scoped
 * SortableContext so drag reordering stays within the container.
 *
 * Recursively renders nested containers (up to MAX_CONTAINER_DEPTH).
 */

import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Plus } from 'lucide-react'
import { isContainerType } from '@zaviago/storefront-config'
import { SectionListItem } from './SectionListItem'
import { SortableBlockLayerRows } from './SectionList'
import type { EditorSection } from './usePageSections'

interface ContainerChildrenProps {
  /** The container's direct child sections */
  childSections: EditorSection[]
  /** The parent container's _editorId */
  parentEditorId: string
  /** Parent's 1-based display index (for hierarchical labels like "2.1") */
  parentIndex: number
  /** Depth level of these children (1 = direct children of root container) */
  depth: number
  /** Currently selected section ID */
  selectedId: string | null
  onSelect: (editorId: string) => void
  onDelete: (editorId: string) => void
  /** Set of expanded container editorIds */
  expandedContainers: Set<string>
  onToggleExpand: (editorId: string) => void
  /** Open widget library scoped to a container */
  onAddChild: (parentEditorId: string, depth: number) => void
  /** Full sections tree for context menu */
  sections: EditorSection[]
  onMoveToContainer: (editorId: string, containerId: string) => void
  onMoveToRoot: (editorId: string) => void
  onDuplicate: (editorId: string) => void
  onSelectFirstBlock?: (editorId: string) => void
}

export function ContainerChildren({
  childSections,
  parentEditorId,
  parentIndex,
  depth,
  selectedId,
  onSelect,
  onDelete,
  expandedContainers,
  onToggleExpand,
  onAddChild,
  sections,
  onMoveToContainer,
  onMoveToRoot,
  onDuplicate,
  onSelectFirstBlock,
}: ContainerChildrenProps) {
  return (
    <SortableContext
      items={childSections.map((c) => c._editorId)}
      strategy={verticalListSortingStrategy}
    >
      <div className="flex flex-col gap-1.5">
        {childSections.map((child, i) => {
          const childLabel = `${parentIndex}.${i + 1}`
          const isChildContainer = isContainerType(child.type)
          const childBlocks = child.blocks ?? []
          const hasChildBlocks = childBlocks.length > 0
          const isChildExpanded = expandedContainers.has(child._editorId)
          const grandchildren = child.children ?? []

          return (
            <div key={child._editorId}>
              <SectionListItem
                section={child}
                indexLabel={childLabel}
                depth={depth}
                isContainer={isChildContainer}
                hasBlocks={hasChildBlocks}
                isExpanded={isChildExpanded}
                childCount={isChildContainer ? grandchildren.length : childBlocks.length}
                isSelected={selectedId === child._editorId}
                onSelect={onSelect}
                onDelete={onDelete}
                onToggleExpand={onToggleExpand}
                sections={sections}
                onMoveToContainer={onMoveToContainer}
                onMoveToRoot={onMoveToRoot}
                onAddChild={onAddChild}
                onDuplicate={onDuplicate}
                onSelectFirstBlock={onSelectFirstBlock}
              />

              {/* Sortable block layer rows for nested block-enabled sections */}
              {hasChildBlocks && !isChildContainer && isChildExpanded && (
                <SortableBlockLayerRows
                  blocks={childBlocks}
                  sectionEditorId={child._editorId}
                  sectionType={child.type}
                  depth={depth}
                  onSelectSection={() => onSelect(child._editorId)}
                />
              )}

              {/* Recursively render grandchildren + inline add for nested containers */}
              {isChildContainer && (isChildExpanded || grandchildren.length === 0) && (
                <>
                  {grandchildren.length > 0 && (
                    <ContainerChildren
                      childSections={grandchildren}
                      parentEditorId={child._editorId}
                      parentIndex={i + 1}
                      depth={depth + 1}
                      selectedId={selectedId}
                      onSelect={onSelect}
                      onDelete={onDelete}
                      expandedContainers={expandedContainers}
                      onToggleExpand={onToggleExpand}
                      onAddChild={onAddChild}
                      sections={sections}
                      onMoveToContainer={onMoveToContainer}
                      onMoveToRoot={onMoveToRoot}
                      onDuplicate={onDuplicate}
                      onSelectFirstBlock={onSelectFirstBlock}
                    />
                  )}
                  {grandchildren.length === 0 && (
                    <button
                      onClick={() => onAddChild(child._editorId, depth + 1)}
                      className="flex items-center gap-1 border-b border-dashed border-gray-200 px-2 py-1.5 text-xs text-gray-400 transition-colors hover:text-sky-500"
                      style={{ paddingLeft: (depth + 1) * 20 }}
                    >
                      <Plus className="h-3 w-3" />
                      Add section
                    </button>
                  )}
                </>
              )}
            </div>
          )
        })}
        {/* Inline add-child button at bottom of container's children */}
        <button
          onClick={() => onAddChild(parentEditorId, depth)}
          className="flex items-center gap-1 border-b border-dashed border-gray-200 px-2 py-1.5 text-xs text-gray-400 transition-colors hover:text-sky-500"
          style={{ paddingLeft: depth * 20 }}
        >
          <Plus className="h-3 w-3" />
          Add section
        </button>
      </div>
    </SortableContext>
  )
}
